import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  Button,
  CircularProgress,
  useTheme
} from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  useGetPipelinesForUserQuery
} from 'src/services/dealpipeline-crm-service'

import usePlan from 'src/hooks/usePlan'
import PipelineCreationFlow from './PipelineCreationFlow'

// Utility to map raw pipeline to consistent selection object
const mapPipelineToSelection = (p) => ({
  id: p._id,
  label: p.title,
  title: p.title,
  totalValue: p.totalValue || 0,
  labels: p.labels,
  stages: p.stages || [],
  type: p.type || 'default',
  createdAt: p.createdAt,
  updatedAt: p.updatedAt
})

const PipelineSelector = ({
  onDropdownClick,
  dropdownAnchor,
  onDropdownClose,
  setSelectedPipeline,
  workspaceId,
  userId,
  openCreatePipelineModal,
  closeCreatePipelineModal,
  pipelineDeleted,
  onResetPipelineDeleted,
  labels,
  deletedPipeline,
  setPipelineData
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedDealPipeline, setSelectedDealPipeline] = useState(null)
  const theme = useTheme()
  const { planDetails } = usePlan()

  const { data: dealPipelines, isLoading: isLoadingPipelines, refetch: refetchPipelines } = useGetPipelinesForUserQuery({
    workspaceId,
    userId
  })

  const dropdownOptions = dealPipelines
    ? dealPipelines.map(mapPipelineToSelection)
    : []

  // Memoize the function that handles pipeline selection and local storage updates
  const handlePipelineSelection = useCallback((pipeline) => {
    setSelectedPipeline(pipeline)
    setSelectedDealPipeline(pipeline)

    if (typeof window !== 'undefined' && typeof window.localStorage !== 'undefined' && pipeline) {
      window.localStorage.setItem('lastSelectedPipelineId', pipeline.id)
    }
  }, [setSelectedPipeline])

  // Memoize the function that resets selections when no pipelines exist
  const resetSelections = useCallback(() => {
    setSelectedPipeline(null)
    setSelectedDealPipeline(null)

    if (typeof window !== 'undefined' && typeof window.localStorage !== 'undefined') {
      window.localStorage.removeItem('lastSelectedPipelineId')
    }
  }, [setSelectedPipeline])

  // Effect for initializing pipeline selection when data loads
  useEffect(() => {
    if (!dealPipelines) return

    if (dealPipelines.length === 0) {
      resetSelections()
      return
    }

    // Try to load previously selected pipeline from localStorage
    const savedPipelineId = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
      ? window.localStorage.getItem('lastSelectedPipelineId')
      : null

    const savedPipeline = savedPipelineId
      ? dealPipelines.find(p => p._id === savedPipelineId)
      : null

    if (savedPipeline) {
      handlePipelineSelection(mapPipelineToSelection(savedPipeline))
    } else {
      // Default to first pipeline if saved one not found
      handlePipelineSelection(mapPipelineToSelection(dealPipelines[0]))
    }
  }, [dealPipelines, handlePipelineSelection, resetSelections])

  // Effect for modal state
  useEffect(() => {
    setIsModalOpen(openCreatePipelineModal)
  }, [openCreatePipelineModal])

  // Effect for handling pipeline deletion
  useEffect(() => {
    if (!pipelineDeleted || !deletedPipeline) return

    const deletedId = deletedPipeline._id

    const handlePipelineDeletion = async () => {
      try {
        const { data } = await refetchPipelines({ force: true })

        if (!data || data.length === 0) {
          resetSelections()
          setPipelineData && setPipelineData(null)
        } else {
          // Filter out the deleted pipeline and select the first available one
          const remainingPipelines = data.filter(p => p._id !== deletedId)

          if (remainingPipelines.length > 0) {
            handlePipelineSelection(mapPipelineToSelection(remainingPipelines[0]))
          } else {
            resetSelections()
            setPipelineData && setPipelineData(null)
          }
        }

        onResetPipelineDeleted()
      } catch (error) {
        onResetPipelineDeleted()
      }
    }

    handlePipelineDeletion()
  }, [pipelineDeleted, deletedPipeline?._id, resetSelections, handlePipelineSelection, onResetPipelineDeleted, setPipelineData])

  const handlePipelineChange = (option) => {
    if (option.id === selectedDealPipeline?.id) {
      onDropdownClose(option) // just close if same
      return
    }

    handlePipelineSelection(option)
    onDropdownClose(option) // Close the menu
  }

  const pipelineLimitReached = () => {
    if (!planDetails || !dealPipelines) return false

    return (
      (planDetails.isFreeUser && dealPipelines.length >= 1) ||
      (planDetails.isStarterPlan && dealPipelines.length >= 5) ||
      (planDetails.isGrowthPlan && dealPipelines.length >= 10)
    )
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Button
        onClick={onDropdownClick}
        endIcon={<ArrowDropDownIcon />}
        sx={{
          backgroundColor: 'white',
          borderRadius: '10px',
          padding: '2px 12px',
          color: '#111927',
          fontWeight: '500',
          textTransform: 'none',
          border: '2px solid #3F50F8'
        }}
      >
        {(dropdownOptions.length > 0 &&
          dropdownOptions.find((option) => option.id === selectedDealPipeline?.id)?.label) ||
          'Select Pipeline'}
        <Typography
          sx={{
            fontWeight: '700',
            marginLeft: '5px',
            color: '#4caf50'
          }}
        >
          <Typography
            sx={{
              fontWeight: '700',
              backgroundColor: '#DAEFDF',
              color: '#4caf50',
              padding: '4px',
              borderRadius: '6px'
            }}
          >
            ${' '}
            {(selectedDealPipeline?.totalValue ?? 0).toLocaleString()}
          </Typography>
        </Typography>
      </Button>
      <Menu
        anchorEl={dropdownAnchor}
        open={Boolean(dropdownAnchor)}
        onClose={() => onDropdownClose(null)}
        sx={{
          width: '100%'
        }}
      >
        {isLoadingPipelines
          ? (
            <MenuItem>
              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <CircularProgress size={20} sx={{ color: '#8181B0' }} />
              </Box>
            </MenuItem>
            )
          : (
              dropdownOptions.map((option) => (
                <MenuItem key={option.id} onClick={() => handlePipelineChange(option)}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                    <Typography>{option.label}</Typography>
                    {option.totalValue !== undefined && (
                      <Typography
                        sx={{
                          fontWeight: '700',
                          backgroundColor: '#DAEFDF',
                          color: '#4caf50',
                          padding: '4px',
                          borderRadius: '6px'
                        }}
                      >
                        $ {option.totalValue}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))
            )}

        <Button
          variant='contained'
          sx={{
            backgroundColor: pipelineLimitReached()
              ? '#bdbdbd' // Greyed-out for disabled state
              : `${theme.palette.primary.main}`, // Default button color
            color: 'white',
            borderRadius: '8px',
            fontWeight: '600',
            margin: '10px',
            width: '90%',
            '&:hover': {
              backgroundColor: pipelineLimitReached()
                ? '#bdbdbd' // Prevent hover effect when disabled
                : `${theme.palette.primary.dark}`, // Default hover color,
              boxShadow: 10
            }
          }}
          disabled={pipelineLimitReached()}
          onClick={() => setIsModalOpen(true)}
        >
          Create New Pipeline
        </Button>
      </Menu>

      <PipelineCreationFlow
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          closeCreatePipelineModal()
        }}
        workspaceId={workspaceId}
        userId={userId}
      />
    </Box>
  )
}

export default PipelineSelector
